import { jsPDF } from "jspdf";

/** Turns any blueprint value into readable plain text lines. */
function valueToText(value: unknown): string {
  if (value === null || value === undefined) return "";
  if (Array.isArray(value)) {
    return value
      .map((v) => `• ${typeof v === "object" ? JSON.stringify(v) : String(v)}`)
      .join("\n");
  }
  if (typeof value === "object") return JSON.stringify(value, null, 2);
  return String(value);
}

export type BlueprintPdfInput = {
  title: string;
  summary?: string | null;
  niche?: string | null;
  tags?: string[] | null;
  score?: number | null;
  blueprint: Record<string, unknown>;
};

/**
 * Builds a launch-blueprint PDF in the browser and triggers the download.
 * No backend call and no credits — the data is already on the page.
 */
export function downloadBlueprintPdf(input: BlueprintPdfInput): string {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const margin = 48;
  const width = doc.internal.pageSize.getWidth() - margin * 2;
  const bottom = doc.internal.pageSize.getHeight() - margin;
  let y = margin;

  const write = (text: string, size: number, bold = false, gap = 6) => {
    doc.setFont("helvetica", bold ? "bold" : "normal");
    doc.setFontSize(size);
    for (const line of doc.splitTextToSize(text, width)) {
      if (y > bottom) {
        doc.addPage();
        y = margin;
      }
      doc.text(line, margin, y);
      y += size * 1.35;
    }
    y += gap;
  };

  write("Trends Discovery — Launch Blueprint", 10, true, 2);
  write(input.title, 20, true, 4);

  const meta = [
    input.niche ? `Niche: ${input.niche}` : null,
    typeof input.score === "number" ? `Score: ${Math.round(input.score)}` : null,
    input.tags?.length ? `Tags: ${input.tags.join(", ")}` : null,
  ]
    .filter(Boolean)
    .join("   |   ");
  if (meta) write(meta, 9, false, 8);
  if (input.summary) write(input.summary, 11, false, 12);

  for (const [key, value] of Object.entries(input.blueprint)) {
    const text = valueToText(value);
    if (!text) continue;
    write(key.replace(/_/g, " ").toUpperCase(), 11, true, 2);
    write(text, 10, false, 12);
  }

  write(
    `Generated ${new Date().toLocaleDateString()} · trendsdiscovery.com · one-time $39 lifetime licence`,
    8,
  );

  const fileName = `blueprint-${input.title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 60) || "trend"}.pdf`;
  doc.save(fileName);
  return fileName;
}
